import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Box, Typography, IconButton, Divider, Grid2 } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { GoogleMap, useJsApiLoader, InfoWindow } from "@react-google-maps/api";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { useTranslation } from "react-i18next";
import Cookies from "universal-cookie";

import { GOOGLE_API_KEY } from "../../Static/Constants/GoogleConfig";
import Loader from "../../Helper/Loaders/Loader";
import CartLoader from "../../Helper/Loaders/CartLoader";
import MapStyles from "../../PagesContent/Map/mapStyles";
import { StyledCard } from "./components/StyledComponents";
import { PrimaryTooltip } from "../../Components/StyledComponent";
import RedirectIcon from "../../Components/Icons/RedirectIcon";
import LocateVehiclesIcon from "../../Components/Icons/Overview/LocateVehiclesIcon";
import InfoIconTooltip from "../../Components/Icons/InfoIconTooltip";
import BusIcon from "../../Assets/Icons/Map/Icons/BusIcon.png";
import BatteryIcon from "../../Assets/Icons/Map/Icons/batteryIcon.png";
import { isTruckMapIcon, truckMapPinUrl } from "../../Helper/Map/truckMapPin";
import TowerLowSoc from "../../Assets/Icons/Map/Icons/Tower_low_soc.png";
import TowerMediumSoc from "../../Assets/Icons/Map/Icons/Tower_medium_soc.png";
import TowerHighSoc from "../../Assets/Icons/Map/Icons/Tower_high_soc.png";
import { getDigitalTwinList } from "../../Api/DigitalTwinListApi";
import MapDigitalTwinSearch from "../../PagesContent/Overview/components/MapDigitalTwinSearch";
import MapFiltersSelection from "./components/MapFiltersSelection";
import { useNavigate } from "react-router-dom";
import TextWithCopy from "../../Features/Table/GenericDraggableTable/Renderers/columnTypes/TextWithCopy";
import { encryptTheParams } from "../../Helper/QueryParams/EncryptDecrypt";
import { FullscreenEnterIcon, FullscreenExitIcon } from "../../PagesContent/Overview/components/FullScreen";
import CustomCopyButton from "../../PagesContent/Overview/components/CustomCopyButton";
import { useBaseEntityInfo } from "../../Hooks/usePagesContent";
import { TableColumnValueRenderer } from "../../Features/Table/GenericDraggableTable/Renderers/TableColumnValueRenderer";
import AppCache from "../../Helper/Common/AppCache";

// ─── Mapping from pinPopupFields config keys → fetch/display definition ───────
//
// fetchColumn: the column name to request from getDigitalTwinList
// dataIndex:   key in the response object
// label:       label shown in the info window
// unit:        optional suffix appended to the value
//
const PIN_POPUP_FIELD_DEFS = {
  busNumber:      { fetchColumn: "licensePlateNo", dataIndex: "licensePlateNo", label: "Bus Number:" },
  chassisNumber:  { fetchColumn: "vin",            dataIndex: "vin",            label: "Chassis No:",      copyable: true },
  liveLocation:   { fetchColumn: "location",       dataIndex: "location",       label: "Live Location:" },
  bmsStatus:      { fetchColumn: "batteryStatus",  dataIndex: "batteryStatus",  label: "BMS Status:" },
  soc:            { fetchColumn: "soc",            dataIndex: "soc",            label: "SOC:",             unit: "%" },
  batteryTemp:    { fetchColumn: "temperature",    dataIndex: "temperature",    label: "Battery Temp:",    unit: "°C" },
  batteryCurrent: { fetchColumn: "current",        dataIndex: "current",        label: "Battery Current:", unit: "A" },
  batteryVoltage: { fetchColumn: "voltage",        dataIndex: "voltage",        label: "Battery Voltage:", unit: "V" },
};

// ─── Map helpers (shared with Coulomb version) ─────────────────────────────────

const getDefaultCenter = () => {
  const cookies = new Cookies();
  const timeZone = cookies.get("timeZone") || "";
  if (timeZone.startsWith("America/")) return { lat: 39.5,  lng: -98.35 };
  if (timeZone.startsWith("Europe/"))  return { lat: 51.0,  lng: 10.0 };
  if (timeZone.startsWith("Australia/")) return { lat: -25.0, lng: 133.0 };
  if (timeZone.startsWith("Africa/"))  return { lat: 8.0,   lng: 20.0 };
  if (timeZone.startsWith("Pacific/")) return { lat: -15.0, lng: -170.0 };
  if (timeZone.startsWith("Asia/"))    return { lat: 34.0,  lng: 100.0 };
  return { lat: 23.2599, lng: 77.4126 };
};

const defaultCenter = getDefaultCenter();

const createClusterIcon = (count, isHover = false) => {
  const size = 40;
  const radius = size / 2;
  const fillColor = isHover ? "#4D44D8" : "#6D72F6";
  const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
  <defs><style>.label{font-family:'Roboto',Arial,sans-serif;font-size:14px;font-weight:600;line-height:22px;}</style></defs>
  <circle cx="${radius}" cy="${radius}" r="${radius - 2}" fill="${fillColor}" opacity="0.95"/>
  <text x="${radius}" y="${radius}" text-anchor="middle" dominant-baseline="central" class="label" fill="#ffffff">${count}</text>
</svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new window.google.maps.Size(size, size),
    anchor: new window.google.maps.Point(radius, radius),
  };
};

const createTintedBusIcon = (tintColor, baseEntityType, mapIconType) => {
	if (isTruckMapIcon(mapIconType)) return truckMapPinUrl(tintColor);
  const icon = baseEntityType?.toLowerCase() === "battery" ? BatteryIcon : BusIcon;
  const r = parseInt(tintColor.slice(1, 3), 16) / 255;
  const g = parseInt(tintColor.slice(3, 5), 16) / 255;
  const b = parseInt(tintColor.slice(5, 7), 16) / 255;
  const svg = `
    <svg width="32" height="32" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
      <defs>
        <filter id="tint">
          <feColorMatrix type="matrix" values="${r} 0 0 0 0 0 ${g} 0 0 0 0 0 ${b} 0 0 0 0 0 0.8 0"/>
        </filter>
      </defs>
      <image xlink:href="${icon}" width="32" height="32" filter="url(#tint)"/>
    </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const getTowerIcon = (soc) => {
  if (soc < 30) return TowerLowSoc;
  if (soc < 50) return TowerMediumSoc;
  return TowerHighSoc;
};

// ─── Component ─────────────────────────────────────────────────────────────────

/**
 * Custom platform MapCard.
 *
 * Differences from the Coulomb version:
 *   1. Filter definitions come from `overviewMap.filters` in customObservabilityConfig
 *      (AppCache) rather than being fetched from the Inventory page metadata API.
 *   2. Info window fields are driven by `overviewMap.pinPopupFields` from the config
 *      rather than being hardcoded. Only fields listed in pinPopupFields are fetched
 *      and displayed.
 */
const MapCard = ({ globalFilters }) => {
  const { t } = useTranslation(["common", "digital_twin"]);
  const { baseEntityTypePluralLable, baseEntityType, baseEntityTypeIDLable, mapIconType } = useBaseEntityInfo();
  const isVehicleEntity = baseEntityType?.toLowerCase() === "vehicle";
  const navigate = useNavigate();
  const cardRef = useRef(null);

  // Read map config from AppCache (set during login from customDashboards[activeKey].overview.overviewMap)
  const customObsConfig = AppCache.getItem("customObservabilityConfig") || {};
  const overviewMapConfig = customObsConfig?.overview?.overviewMap || {};
  const filterConfigs = Array.isArray(overviewMapConfig.filters) ? overviewMapConfig.filters : [];
  const pinPopupFields = Array.isArray(overviewMapConfig.pinPopupFields) ? overviewMapConfig.pinPopupFields : [];

  // Resolve which field defs are active based on pinPopupFields config
  const activePinFields = useMemo(
    () => pinPopupFields.map((key) => PIN_POPUP_FIELD_DEFS[key]).filter(Boolean),
    [pinPopupFields]
  );

  // Columns to fetch for the detail popup (always include base positioning + ID columns)
  const detailFetchColumns = useMemo(() => {
    const base = ["latitude", "longitude", "deviceID", "batteryID"];
    const fromConfig = activePinFields.map((f) => f.fetchColumn).filter(Boolean);
    if (isVehicleEntity) base.push("vin");
    return [...new Set([...base, ...fromConfig])];
  }, [activePinFields, isVehicleEntity]);

  const [filterBy, setFilterBy] = useState("busNumber");
  const [searchValue, setSearchValue] = useState("");
  const [map, setMap] = useState(null);
  const [selectedDigitalTwin, setSelectedDigitalTwin] = useState(null);
  const [selectedDigitalTwinDetail, setSelectedDigitalTwinDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showInfoWindow, setShowInfoWindow] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [digitalTwinData, setDigitalTwinData] = useState([]);
  const [digitalTwinDataForGlobalFilters, setDigitalTwinDataForGlobalFilters] = useState([]);
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: GOOGLE_API_KEY });
  const [searchTerm, setSearchTerm] = useState({ selectedDevices: [], selectedSearchColumn: null });
  const [isFull, setIsFull] = useState(false);
  const [isFullscreenHovered, setIsFullscreenHovered] = useState(false);
  const [loading, setLoading] = useState(false);

  const filteredDigitalTwins = useMemo(() => {
    if (!searchValue) return digitalTwinData;
    return digitalTwinData.filter((v) =>
      v[filterBy]?.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [digitalTwinData, searchValue, filterBy]);

  const formatDigitalTwinList = (data) =>
    data.filter((item) => {
      const lat = parseFloat(item.latitude);
      const lng = parseFloat(item.longitude);
      return !isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180 && !(lat === 0 && lng === 0);
    });

  const fetchDigitalTwinList = async (applyGlobalFiltersOnly = false) => {
    if (applyGlobalFiltersOnly) setLoading(true);
    const searchID = searchTerm.selectedSearchColumn || "searchID";
    const filters = { tag_battery_status: ["Operational", "Non-Operational"], ...selectedFilters };
    const columns = ["latitude", "longitude", "soc", "deviceID", "batteryID"];
    const response = await getDigitalTwinList(
      { filters, pageNumber: 1, rowsPerPage: 1, sortBy: "tag_battery_status_updatedBy", skipPagination: true, columns, [searchID]: searchTerm.selectedDevices },
      globalFilters
    );
    const formatted = formatDigitalTwinList(response?.response?.data || []);
    setDigitalTwinData(formatted);
    if (applyGlobalFiltersOnly) {
      setDigitalTwinDataForGlobalFilters(formatted);
      setLoading(false);
    }
  };

  const fetchDigitalTwinDetail = async (pin) => {
    setDetailLoading(true);
    setSelectedDigitalTwinDetail(null);
    const response = await getDigitalTwinList(
      {
        filters: { tag_battery_status: ["Operational", "Non-Operational"] },
        pageNumber: 1, rowsPerPage: 1,
        sortBy: "tag_battery_status_updatedBy",
        skipPagination: true,
        columns: detailFetchColumns,
        batteryID: [pin.batteryID],
      },
      globalFilters
    );
    const data = response?.response?.data || [];
    setSelectedDigitalTwinDetail(data[0] || null);
    setDetailLoading(false);
    setShowInfoWindow(true);
  };

  useEffect(() => {
    if (Object.keys(globalFilters).length === 0) return;
    fetchDigitalTwinList(true);
  }, [globalFilters, searchTerm, selectedFilters]);

  useEffect(() => {
    if (!isLoaded || !map) return;
    if (map.__busCluster) map.__busCluster.clearMarkers();
    const g = window.google;
    const isTowerIcon = mapIconType === "tower";
    const normalBusIcon = { url: createTintedBusIcon("#6D72F6", baseEntityType, mapIconType), scaledSize: new g.maps.Size(32, 32), anchor: new g.maps.Point(16, 0) };
    const hoverBusIcon  = { url: createTintedBusIcon("#4D44D8", baseEntityType, mapIconType), scaledSize: new g.maps.Size(32, 32), anchor: new g.maps.Point(16, 0) };

    const markers = filteredDigitalTwins.map((v) => {
      const markerIcon = isTowerIcon
        ? { url: getTowerIcon(v.soc), scaledSize: new g.maps.Size(32, 32), anchor: new g.maps.Point(16, 32) }
        : normalBusIcon;
      const marker = new g.maps.Marker({ position: { lat: v.latitude, lng: v.longitude }, icon: markerIcon });
      marker.addListener("click", () => { setSelectedDigitalTwin(v); fetchDigitalTwinDetail(v); });
      if (!isTowerIcon) {
        marker.addListener("mouseover", () => marker.setIcon(hoverBusIcon));
        marker.addListener("mouseout",  () => marker.setIcon(normalBusIcon));
      }
      return marker;
    });

    if (filteredDigitalTwins.length) {
      const clusterer = new MarkerClusterer({
        map, markers,
        renderer: {
          render: ({ count, position }) => {
            const cm = new g.maps.Marker({ position, icon: createClusterIcon(count, false), label: { text: String(count), color: "transparent" } });
            cm.addListener("mouseover", () => cm.setIcon(createClusterIcon(count, true)));
            cm.addListener("mouseout",  () => cm.setIcon(createClusterIcon(count, false)));
            return cm;
          },
        },
      });
      map.__busCluster = clusterer;
    }

    const bounds = new g.maps.LatLngBounds();
    markers.forEach((m) => bounds.extend(m.getPosition()));
    if (!bounds.isEmpty()) map.fitBounds(bounds);

    return () => {
      if (map.__busCluster) {
        map.__busCluster.clearMarkers();
        map.__busCluster = null;
      }
      markers.forEach((m) => m.setMap(null));
    };
  }, [isLoaded, map, filteredDigitalTwins]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) cardRef.current?.requestFullscreen();
    else document.exitFullscreen();
  };

  useEffect(() => {
    const handler = () => {
      setIsFull(!!document.fullscreenElement);
      if (map) setTimeout(() => window.google.maps.event.trigger(map, "resize"), 300);
    };
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, [map]);

  const onLoad    = useCallback((m) => setMap(m), []);
  const onUnmount = useCallback(() => setMap(null), []);
  const handleToggleClose = () => { setShowInfoWindow(false); setSelectedDigitalTwinDetail(null); };
  const handleDigitalTwinSearch = (devices, col) => setSearchTerm({ selectedDevices: devices, selectedSearchColumn: col });
  const handleFilters = (f) => setSelectedFilters(f);

  if (!isLoaded) {
    return <Box sx={{ height: "786px" }}><Loader /></Box>;
  }

  const displayID = isVehicleEntity
    ? selectedDigitalTwinDetail?.licensePlateNo
    : selectedDigitalTwinDetail?.deviceID;

  return (
    <StyledCard
      ref={cardRef}
      sx={{
        position: "relative",
        background: "#fff",
        width: "100%",
        height: isFull ? "100%" : "auto",
        display: "flex",
        flexDirection: "column",
        borderRadius: isFull ? 0 : "8px",
        gap: 1,
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LocateVehiclesIcon sx={{ color: "#6D72F6" }} />
          <Typography variant="fw500s16lh20c000000">Locate {baseEntityTypePluralLable}</Typography>
          <InfoIconTooltip title={`Only ${baseEntityTypePluralLable} that have reported their GPS location at least once are displayed on this map.`} />
          <PrimaryTooltip title={`${baseEntityTypePluralLable} with Geo Location`}>
            <Box sx={{ px: 1, background: "#EEEFFF", borderRadius: "6px", display: "flex", alignItems: "center" }}>
              <Typography variant="fw600s12lh24c6D72F6">{digitalTwinDataForGlobalFilters.length}</Typography>
            </Box>
          </PrimaryTooltip>
        </Box>
        <MapDigitalTwinSearch
          onSearch={handleDigitalTwinSearch}
          searchableFields={overviewMapConfig.searchableFields || undefined}
          container={isFull ? cardRef.current : undefined}
        />
      </Box>

      {/* Map canvas */}
      <Box sx={{ flexGrow: 1, height: isFull ? "calc(100% - 64px)" : "584px", position: "relative" }}>
        {(loading || detailLoading) && (
          <Box sx={{ position: "absolute", inset: 0, zIndex: 10, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "rgba(255,255,255,0.6)", borderRadius: "8px" }}>
            <CartLoader />
          </Box>
        )}
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "100%", borderRadius: "8px" }}
          center={defaultCenter}
          zoom={5}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={{ fullscreenControl: false, styles: MapStyles, minZoom: 2, maxZoom: 22, disableDefaultUI: true, zoomControl: true }}
        >
          {/* Filters — driven by overviewMap.filters from config */}
          <MapFiltersSelection
            filterConfigs={filterConfigs}
            selectedFilters={selectedFilters}
            onApplyFilters={handleFilters}
            searchResultCount={filteredDigitalTwins.length}
            container={isFull ? cardRef.current : undefined}
          />

          {/* Fullscreen toggle */}
          <IconButton
            onClick={toggleFullscreen}
            onMouseEnter={() => setIsFullscreenHovered(true)}
            onMouseLeave={() => setIsFullscreenHovered(false)}
            sx={{ position: "absolute", padding: 0, top: 8, right: 8, bgcolor: "transparent", width: "29", height: "29", "&:hover": { bgcolor: "transparent" } }}
          >
            {isFull ? <FullscreenExitIcon isHover={isFullscreenHovered} /> : <FullscreenEnterIcon isHover={isFullscreenHovered} />}
          </IconButton>

          {/* Pin info window — fields driven by overviewMap.pinPopupFields */}
          {selectedDigitalTwin && showInfoWindow && (
            <InfoWindow
              position={{ lat: selectedDigitalTwin.latitude, lng: selectedDigitalTwin.longitude }}
              onCloseClick={handleToggleClose}
            >
              <Box
                sx={{
                  minWidth: "388px",
                  py: 2, px: 2.5,
                  borderRadius: "8px",
                  backgroundColor: "#FFFFFF",
                  boxShadow: "0px 8px 16px -2px rgba(27, 33, 44, 0.12)",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                }}
              >
                {/* ID header + redirect */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography variant="fw500s14c2E2E2E" sx={{ lineHeight: "16px" }}>
                      {baseEntityTypeIDLable}:&nbsp;
                    </Typography>
                    {isFull ? (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography variant="fw400s14c494949">{displayID}</Typography>
                        <CustomCopyButton text={displayID} />
                      </Box>
                    ) : (
                      <TextWithCopy
                        text={displayID}
                        type={isVehicleEntity ? "License Plate" : "Device ID"}
                        textVariant="fw400s14c494949"
                      />
                    )}
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{ cursor: selectedDigitalTwinDetail ? "pointer" : "default" }}
                      onClick={() => {
                        if (!selectedDigitalTwinDetail) return;
                        const params = {
                          batteryID: selectedDigitalTwinDetail.batteryID,
                          deviceID: selectedDigitalTwinDetail.deviceID,
                          batterySearchedGlobal: "false",
                          prevLink: window.location.pathname + window.location.search,
                        };
                        encryptTheParams(params, navigate, null, "/custom-observability/specificBattery", true);
                      }}
                    >
                      <RedirectIcon sx={{ fontSize: 14, color: selectedDigitalTwinDetail ? "#6D72F6" : "#C0C0C0", width: "14px", height: "14px", mt: 0.5 }} />
                    </Box>
                    <IconButton size="small" onClick={handleToggleClose} sx={{ color: "#6E6E6E" }}>
                      <CloseIcon />
                    </IconButton>
                  </Box>
                </Box>

                <Divider flexItem sx={{ borderColor: "#E4E4E4", my: 1.5 }} />

                {/* Dynamic popup fields from pinPopupFields config */}
                <Grid2 container flexDirection="column" gap={1.5}>
                  {activePinFields.map(({ dataIndex, label, unit, copyable }) => {
                    const rawValue = selectedDigitalTwinDetail?.[dataIndex];
                    const displayValue = rawValue != null
                      ? (unit ? `${rawValue} ${unit}` : String(rawValue))
                      : null;
                    return (
                      <Box key={dataIndex} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="fw400s14c8F8F8F">{label}</Typography>
                        {copyable && displayValue ? (
                          <TextWithCopy text={displayValue} type={label.replace(":", "").trim()} textVariant="fw400s14c000000" />
                        ) : (
                          <Typography variant="fw400s14c000000">{displayValue ?? "-"}</Typography>
                        )}
                      </Box>
                    );
                  })}
                  {/* Lat/Long is always shown */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="fw400s14c8F8F8F">Lat/Long:</Typography>
                    <Typography variant="fw400s14c000000">
                      {selectedDigitalTwinDetail?.latitude}/{selectedDigitalTwinDetail?.longitude}
                    </Typography>
                  </Box>
                </Grid2>
              </Box>
            </InfoWindow>
          )}
        </GoogleMap>
      </Box>
    </StyledCard>
  );
};

export default MapCard;
